import * as Sentry from "@sentry/react";
import type { PostHog } from "posthog-js";

import {
  campaignSessionProperties,
  captureCurrentVisit,
  getCurrentVisit,
  hasCampaignSignal,
  initialProperties,
  readFirstTouch,
  rememberFirstTouch,
  type Attribution,
  type AttributionStorage,
} from "../attribution";
import { analyticsAllowed } from "../consent";
import type { ObservabilityEvent } from "../events";
import {
  productAnalyticsAllowedOnSurface,
  warnKioskCaptureBlocked,
} from "../kiosk-policy";
import type { ObservabilityUser, WebInitOptions } from "../types";

let sentryEnabled = false;
let posthogEnabled = false;
let posthogKey: string | undefined;
let posthogHost: string | undefined;
// posthog-js (~218KB) is imported on demand inside startPostHog so it stays out
// of the eager boot graph and never loads on the kiosk/player surface, where it
// is blocked from ever running. Every consumer below guards on `posthogEnabled`,
// which is only set true once this reference is populated.
let posthog: PostHog | undefined;
// Single-flight guard: boot (initObservability) and the React tree
// (PageViewTracker mount, the consent banner) can all ask for PostHog before
// the dynamic import resolves. One import, one init, one attribution pass.
let posthogStarting: Promise<void> | undefined;

/**
 * The last pageview asked for while PostHog was not yet running. PostHog only
 * comes up after an async import (and, for a first visit, only after the
 * visitor accepts cookies), so the `$pageview` for the route the visitor is on
 * would otherwise be lost — and that is the landing pageview, the one whose
 * href still carries `?utm_*`. Held here and captured once PostHog starts.
 * Never set on kiosk paths (see `trackPageView`).
 */
let pendingPageView:
  | { path: string; properties?: Record<string, unknown> }
  | undefined;

export { Sentry };

/** The workspace group type used for account-level (B2B) analysis in PostHog. */
export const WORKSPACE_GROUP_TYPE = "workspace";

/**
 * Kiosk enforcement (see `kiosk-policy.ts`): the player surface never emits
 * product analytics — not a pageview, not an identify, not a capture. This is
 * checked at every entry point rather than trusted to call sites, so a
 * `track()` added to a player screen later is inert by construction. Sentry is
 * deliberately NOT gated: crash reporting on a venue player is operational.
 */
function analyticsBlockedHere(what: string): boolean {
  const pathname =
    typeof window === "undefined" ? null : window.location?.pathname;
  if (productAnalyticsAllowedOnSurface({ pathname })) return false;
  warnKioskCaptureBlocked(what);
  return true;
}

export function initObservability(options: WebInitOptions): void {
  const {
    sentryDsn,
    posthogKey: key,
    posthogHost: host,
    platform,
    release,
    environment,
  } = options;

  posthogKey = key;
  posthogHost = host;

  // FIX-1: snapshot this page load's utm_*/click-id/referrer/landing path into
  // MEMORY now, before consent exists and before the SPA router drops the
  // query string. Nothing touches storage here — persistence happens in
  // startPostHog(), which only ever runs after consent and off the kiosk.
  // Kiosk paths hold nothing at all (see kiosk-policy.ts).
  if (
    typeof window !== "undefined" &&
    productAnalyticsAllowedOnSurface({ pathname: window.location?.pathname })
  ) {
    captureCurrentVisit({
      search: window.location?.search,
      referrer: typeof document === "undefined" ? null : document.referrer,
      path: window.location?.pathname,
    });
  }

  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: environment ?? "development",
      release,
      // No replayIntegration: session replay is sampled at 0 (captures nothing),
      // yet it was the largest eager chunk (~742KB) AND its DOM/input observers
      // added latency to every keystroke. Dropping the call lets Vite tree-shake
      // the replay bundle out entirely. Re-add with a non-zero sample rate if
      // replay is ever actually wanted.
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: environment === "production" ? 0.1 : 1,
    });
    Sentry.setTag("platform", platform);
    sentryEnabled = true;
  }

  if (key && analyticsAllowed()) {
    void startPostHog(key, host, platform);
  }
}

/**
 * Start product analytics if the visitor has consented. Safe to call from
 * anywhere, any number of times: it is a no-op without a key, without consent,
 * on the kiosk, or when PostHog is already running or starting. Resolves once
 * PostHog is up (or immediately when it will not start).
 */
export function initAnalyticsIfConsented(): Promise<void> {
  if (posthogEnabled || !posthogKey) return Promise.resolve();
  if (!analyticsAllowed()) return Promise.resolve();
  return startPostHog(posthogKey, posthogHost, "web");
}

/** Whether product analytics is actually running (consented, keyed, non-kiosk). */
export function isProductAnalyticsEnabled(): boolean {
  return posthogEnabled;
}

export function shutdownAnalytics(): void {
  pendingPageView = undefined;
  if (!posthogEnabled) return;
  posthog?.opt_out_capturing();
  posthog?.reset();
  posthogEnabled = false;
  posthogStarting = undefined;
}

function startPostHog(
  key: string,
  host: string | undefined,
  platform: string,
): Promise<void> {
  if (posthogEnabled) return Promise.resolve();
  if (analyticsBlockedHere("posthog.init")) return Promise.resolve();
  posthogStarting ??= (async () => {
    const ph = (await import("posthog-js")).default;
    // Consent may have been withdrawn while the chunk was loading.
    if (!analyticsAllowed() || analyticsBlockedHere("posthog.init")) return;
    ph.init(key, {
      api_host: host ?? "https://eu.i.posthog.com",
      persistence: "localStorage+cookie",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      loaded: (client) => {
        client.register({ platform });
      },
    });
    applyAttribution(ph);
    posthog = ph;
    posthogEnabled = true;
    flushPendingPageView();
  })().finally(() => {
    posthogStarting = undefined;
  });
  return posthogStarting;
}

/** Capture the pageview that arrived before PostHog was running, if any. */
function flushPendingPageView(): void {
  const pending = pendingPageView;
  pendingPageView = undefined;
  if (pending) trackPageView(pending.path, pending.properties);
}

/**
 * `localStorage` behind the attribution store's tiny interface. Access itself
 * can throw (blocked storage, sandboxed iframes), so this returns null rather
 * than letting attribution take the page down.
 */
function attributionStorage(): AttributionStorage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** The stored first-touch attribution, if this browser ever landed from a campaign. */
function firstTouchAttribution(): Attribution | null {
  const storage = attributionStorage();
  return storage ? (readFirstTouch(storage)?.attribution ?? null) : null;
}

/**
 * FIX-1, the persist-and-register moment. This is ONLY reached from
 * startPostHog(), i.e. after consent and off the kiosk — the one place where
 * writing attribution to storage is allowed.
 *
 * These super properties are what carries attribution: they ride on every
 * event (including the landing `$pageview` flushed right after this), whether
 * or not the URL still has its query string by the time PostHog is up.
 *
 * - First touch: persisted once (valid for 12 months, never overwritten while
 *   valid), then registered as `initial_*` super properties via
 *   `register_once` into PostHog's long-lived persistence — the one set that
 *   belongs there.
 * - Current visit: the campaign set of THIS page load, registered under plain
 *   keys (`utm_source`, ...) with `register_for_session`, i.e. into
 *   sessionStorage. That is where posthog-js keeps its own non-initial campaign
 *   params (posthog-core.js, capture: "The non-initial versions are stored in
 *   the sessionPersistence, as they are sent with every event and used by the
 *   session table to create session-initial props"). A persistent `register`
 *   would make one week's `utm_source=linkedin` stick to every organic session
 *   that follows. The FULL key set is written, `null` for absent keys, so a
 *   later `?gclid=` visit does not inherit an earlier `utm_campaign`.
 */
function applyAttribution(ph: PostHog): void {
  const visit = getCurrentVisit();
  const storage = attributionStorage();
  if (visit && storage) rememberFirstTouch(storage, visit);

  const first = firstTouchAttribution();
  if (first) ph.register_once(initialProperties(first));
  if (visit && hasCampaignSignal(visit)) {
    ph.register_for_session(campaignSessionProperties(visit));
  }
}

export function setUser(user: ObservabilityUser): void {
  if (sentryEnabled) {
    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
    });
    if (user.workspaceId) {
      Sentry.setTag("workspace_id", user.workspaceId);
    }
  }
  if (posthogEnabled && !analyticsBlockedHere("posthog.identify")) {
    // The third argument is `userPropertiesToSetOnce`: the first-touch
    // attribution lands on the PERSON as `initial_utm_source`,
    // `initial_referrer`, `initial_landing_path`, ... and is never overwritten.
    //
    // This identify is also what makes attribution reach the trustworthy
    // server-side funnel: identify merges the anonymous landing visit (its
    // events carry the `initial_*` / `utm_*` super properties registered in
    // applyAttribution) into the person keyed by the Clerk user id, and every
    // server-side capture (`auth.signed_up`, `trial.started`,
    // `subscription.converted`, the `captureProduct` route events) uses that
    // same Clerk user id as distinct_id, never the local users.id uuid
    // (artifacts/api-server/src/lib/auth.ts, product-events.ts,
    // analytics-identity.ts). No server column, no extra request: the person
    // merge is the join.
    const first = firstTouchAttribution();
    posthog?.identify(
      user.id,
      {
        email: user.email ?? undefined,
        workspace_id: user.workspaceId ?? undefined,
      },
      first ? initialProperties(first) : undefined,
    );
    if (user.workspaceId) setWorkspaceGroup(user.workspaceId);
  }
}

/**
 * Associate this browser's subsequent events with a workspace (PostHog group
 * analytics). Without it, `workspace_id` is only a filterable property —
 * account-level questions ("weekly-active *accounts*", "activation rate per
 * workspace") need the group.
 */
export function setWorkspaceGroup(
  workspaceId: string,
  properties?: Record<string, unknown>,
): void {
  if (!posthogEnabled) return;
  if (analyticsBlockedHere("posthog.group")) return;
  posthog?.group(WORKSPACE_GROUP_TYPE, workspaceId, properties);
}

export function clearUser(): void {
  if (sentryEnabled) {
    Sentry.setUser(null);
  }
  if (posthogEnabled) {
    posthog?.reset();
  }
}

export function captureError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!sentryEnabled) return;
  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(error);
  });
}

export function track(
  event: ObservabilityEvent,
  properties?: Record<string, unknown>,
): void {
  if (!posthogEnabled) return;
  if (analyticsBlockedHere(event)) return;
  posthog?.capture(event, properties);
}

export function trackPageView(
  path: string,
  properties?: Record<string, unknown>,
): void {
  if (analyticsBlockedHere("$pageview")) return;
  if (!posthogEnabled) {
    // PostHog is not up yet (async import, or no consent yet). Keep the latest
    // requested route so startPostHog can capture it; the router calls this
    // again on every navigation, so "latest" is always the current route.
    pendingPageView = { path, properties };
    return;
  }
  // `$current_url` is the FULL href (query string included), matching what
  // posthog-js itself sends on every other event, so web analytics sees the
  // same host/path/query on pageviews. When the href still carries `?utm_*`
  // PostHog's own UTM handling reads it too; when it no longer does (the
  // visitor navigated before consenting) attribution still arrives via the
  // super properties registered in applyAttribution. The wouter path the
  // caller passed rides along as `path`.
  const href =
    typeof window === "undefined" ? undefined : window.location?.href;
  posthog?.capture("$pageview", {
    $current_url: href || path,
    path,
    ...properties,
  });
}

export function installGlobalErrorHandlers(): void {
  if (!sentryEnabled) return;

  window.addEventListener("error", (event) => {
    captureError(event.error ?? event.message, {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureError(event.reason, { source: "unhandledrejection" });
  });
}

export const ObservabilityErrorBoundary = Sentry.ErrorBoundary;
